'use client';

import { useTranslation } from 'react-i18next';

import { formatLocaleDate, formatTimeToAmPm } from '@kit/shared/utils';
import { Badge } from '@kit/ui/badge';
import { cn } from '@kit/ui/utils';

import type { SeasonSession } from '~/lib/class-seasons/class-season-quote';

// YYYY-MM-DD as local noon so no timezone fold moves it to another day.
function parseSessionDate(date: string) {
  const [year = 0, month = 1, day = 1] = date.split('-').map(Number);
  return new Date(year, month - 1, day, 12);
}

/**
 * Numbered season schedule; sessions a purchase doesn't cover (already
 * started, or before the chosen start session) are struck out.
 */
export function SeasonScheduleList({
  sessions,
  className,
}: {
  sessions: SeasonSession[];
  className?: string;
}) {
  const { t, i18n } = useTranslation('bookings');

  return (
    <ol className={cn('divide-y', className)} data-test="season-schedule">
      {sessions.map((session, index) => (
        <li
          key={session.booking_id}
          className="flex items-center gap-4 py-3 text-sm"
        >
          <span className="text-muted-foreground w-5 shrink-0 text-end tabular-nums">
            {index + 1}
          </span>
          <span
            className={cn(
              'flex-1',
              !session.is_included && 'text-muted-foreground line-through',
            )}
          >
            {formatLocaleDate(parseSessionDate(session.date), i18n.language, {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              year: undefined,
            })}
            <span className="text-muted-foreground ms-2">
              {formatTimeToAmPm(session.start_time, i18n.language)}
            </span>
          </span>
          {session.is_included && session.is_full ? (
            <Badge variant="warning">{t('season.full')}</Badge>
          ) : null}
        </li>
      ))}
    </ol>
  );
}
